"use client";

import React from "react";
import { Clock, BarChart3, ExternalLink } from "lucide-react";
import { motion } from "motion/react";
import { TIndustriesConfig } from "@/app/config";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      duration: 0.6
    }
  }
};

const metricsVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
};




export default function EntertainmentIndustryNews({config}:{config:TIndustriesConfig['entertainmentIndustryNews']}) {
  return (
    <motion.section 
      className="py-16 lg:py-20 bg-gray-50"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
           {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* News Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
          variants={containerVariants}
        >
          {config.newsData.map((news, index) => (
            <motion.div
              key={index}
              className={`${news.bgColor} ${news.borderColor} border rounded-xl p-6 cursor-pointer`}
              variants={{
                hidden: { 
                  opacity: 0, 
                  y: 50,
                  scale: 0.9
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.7,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{
                y: -8,
                scale: 1.02,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              whileTap={{ scale: 0.98 }}
              style={{ 
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
              }}
            >
              {/* Category Badge and Metrics */}
              <div className="flex items-center justify-between mb-4">
                <motion.span 
                  className={`${news.categoryColor} text-white text-sm font-semibold px-3 py-1 rounded-full`}
                  variants={{
                    hidden: { opacity: 0, scale: 0.8, x: -20 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        ease: "backOut"
                      }
                    }
                  }}
                  whileHover={{ scale: 1.05 }}
                >
                  {news.category}
                </motion.span>
                <motion.div 
                  className="flex items-center gap-4 text-sm text-gray-500"
                  variants={metricsVariants}
                >
                  <motion.div 
                    className="flex items-center gap-1"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Clock className="w-4 h-4" />
                    <span>{news.readTime}</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-1"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>{news.engagement}</span>
                  </motion.div>
                </motion.div>
              </div>

              {/* Article Title */}
              <motion.h3 
                className="text-xl font-semibold text-gray-900 mb-3 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
              >
                {news.title}
              </motion.h3>

              {/* Article Description */}
              <motion.p 
                className="text-gray-600 text-base mb-4 leading-relaxed"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.4, duration: 0.6 }}
              >
                {news.description}
              </motion.p>

              {/* Footer */}
              <motion.div 
                className="flex items-center justify-between"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
              >
                <motion.span 
                  className="text-sm text-gray-500"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.6, duration: 0.4 }}
                >
                  {news.timeAgo}
                </motion.span>
                <motion.button 
                  className={`${news.readMoreColor} text-sm font-semibold hover:underline flex items-center gap-1 group`}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.6, duration: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Read More
                  <motion.div
                    variants={{
                      rest: { x: 0 },
                      hover: { x: 3 }
                    }}
                    initial="rest"
                    whileHover="hover"
                    transition={{ duration: 0.2 }}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </motion.div>
                </motion.button>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All News Button */}
        <motion.div 
          className="text-center"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: "easeOut"
              }
            }
          }}
        >
          {/* <motion.button 
            className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto group"
            whileHover={{
              scale: 1.05,
              y: -2,
              transition: {
                duration: 0.2,
                ease: "easeOut"
              }
            }}
            whileTap={{ scale: 0.95 }}
          >
            <span>View All Industry News</span>
            <motion.div
              variants={{
                rest: { x: 0, rotate: 0 },
                hover: { x: 3, rotate: 15 }
              }}
              initial="rest"
              whileHover="hover"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <ExternalLink className="w-4 h-4" />
            </motion.div>
          </motion.button> */}
        </motion.div>
      </div>
    </motion.section>
  );
}